/* ─────────────────────────────────────────────────────────────
   FINANCIAL-STATEMENT ROW CONFIG

   Row order, labels and BOLD subtotals are taken from the Forecast Scenario
   Builder v2 artifact (its IS_ROWS / BS_ROWS / CF_ROWS tables). Rows the
   artifact flags `total:1` render bold with a row-background highlight and a
   rule above them — those are the statement subtotals, not arbitrary emphasis.

   Rows the artifact marks "stored in DB — not displayed on the UI" are NOT
   rendered (per Somya, 2026-08-18 — they used to show dimmed below a divider).
   The lists are kept so the keys stay documented and are recognised as known
   (i.e. they don't resurface through the unknown-key fallback, which is also
   hidden now).

   The intrinsic-value endpoint returns the same row shape as
   /stocks/financials/{ticker}, so the alias map normalises the raw payload
   keys onto the canonical ones used below.
───────────────────────────────────────────────────────────── */

/* ── Aliases: raw payload key → canonical key ── */
export const KEY_ALIASES = Object.freeze({
  cost_of_goods_sold: "cost_of_revenue",
  operating_income_ebit: "operating_income",
  eps: "eps_basic",
  shares_outstanding_m: "shares_outstanding",
  // the artifact's income statement names D&A with the long form
  depreciation_and_amortization: "depreciation_amortization",
  // possible raw names for the balance sheet's other-equity-adjustments line
  othertotal_stockholders_equity: "other_equity_adjustments",
  other_stockholders_equity: "other_equity_adjustments",
});

/* ── Income statement — artifact order ── */
const INCOME_ROWS = [
  { key: "revenue", label: "Revenue", bold: true },
  { key: "cost_of_revenue", label: "Cost Of Revenue" },
  { key: "gross_profit", label: "Gross Profit", bold: true },
  { key: "selling_general_and_administrative_expenses", label: "Selling, General & Administrative" },
  { key: "other_operating_expenses", label: "Other Operating Expense" },
  { key: "depreciation_amortization", label: "Depreciation Amortization" },
  { key: "operating_expenses", label: "Operating Expenses" },
  { key: "operating_income", label: "Operating Income", bold: true },
  { key: "interest_income", label: "Interest Income" },
  { key: "interest_expense", label: "Interest Expense" },
  { key: "total_other_income_expenses_net", label: "Other Income/(Expense)" },
  { key: "income_before_tax", label: "Income Before Tax", bold: true },
  { key: "income_tax_expense", label: "Income Tax Expense" },
  { key: "net_income", label: "Net Income", bold: true },
  { key: "bottom_line_net_income", label: "Bottom Line Net Income" },
  { key: "ebitda", label: "EBITDA" },
  { key: "eps_basic", label: "EPS Basic" },
  { key: "eps_diluted", label: "EPS Diluted" },
  { key: "shares_outstanding", label: "Shares Outstanding" },
  { key: "shares_outstanding_diluted", label: "Shares Outstanding Diluted" },
  { key: "gross_margin", label: "Gross Margin" },
  { key: "operating_margin", label: "Operating Margin" },
  { key: "ebitda_margin", label: "EBITDA Margin" },
  { key: "net_margin", label: "Net Margin" },
];

/* Rows the artifact stores but does not show on its UI. */
const INCOME_STORED = [
  { key: "research_and_development_expenses", label: "R&D expenses" },
  { key: "general_and_administrative_expenses", label: "G&A expenses" },
  { key: "ebit", label: "EBIT" },
  { key: "net_income_deductions", label: "Net income deductions (raw NCI col)" },
  { key: "net_income_from_continuing_operations", label: "NI from continuing ops" },
  { key: "net_income_from_discontinued_operations", label: "NI from discontinued ops" },
  { key: "minority_interest", label: "Minority Interest" },
];

/* ── Balance sheet — artifact order ── */
const BALANCE_ROWS = [
  { key: "cash_and_equivalents", label: "Cash And Equivalents" },
  { key: "short_term_investments", label: "Short Term Investments" },
  { key: "accounts_receivable", label: "Accounts Receivable" },
  { key: "inventory", label: "Inventory" },
  { key: "other_current_assets", label: "Other Current Assets" },
  { key: "total_current_assets", label: "Total Current Assets", bold: true },
  { key: "property_plant_equipment", label: "Property Plant Equipment" },
  { key: "intangible_assets", label: "Intangible Assets" },
  { key: "goodwill", label: "Goodwill" },
  { key: "long_term_investments", label: "Long Term Investments" },
  { key: "other_non_current_assets", label: "Other Non Current Assets" },
  { key: "total_non_current_assets", label: "Total Non Current Assets", bold: true },
  { key: "total_assets", label: "Total Assets", bold: true },
  { key: "accounts_payable", label: "Accounts Payable" },
  { key: "short_term_debt", label: "Short Term Debt" },
  { key: "other_current_liabilities", label: "Other Current Liabilities" },
  { key: "total_current_liabilities", label: "Total Current Liabilities", bold: true },
  { key: "long_term_debt", label: "Long Term Debt" },
  { key: "other_non_current_liabilities", label: "Other Non Current Liabilities" },
  { key: "total_non_current_liabilities", label: "Total Non Current Liabilities", bold: true },
  { key: "total_liabilities", label: "Total Liabilities", bold: true },
  { key: "common_stock", label: "Common Stock" },
  { key: "retained_earnings", label: "Retained Earnings" },
  { key: "other_equity_adjustments", label: "Other Equity Adjustments" },
  { key: "total_stockholders_equity", label: "Total Stockholders Equity", bold: true },
  { key: "minority_interest", label: "Minority Interest" },
  { key: "total_equity", label: "Total Equity", bold: true },
];

const BALANCE_STORED = [
  { key: "deferred_revenue", label: "Deferred revenue" },
  { key: "total_debt", label: "Total debt" },
  { key: "net_debt", label: "Net debt" },
  { key: "additional_paid_in_capital", label: "Additional paid-in capital" },
  { key: "accumulated_other_comprehensive_income_loss", label: "Accumulated OCI" },
  { key: "treasury_stock", label: "Treasury stock" },
  { key: "preferred_stock", label: "Preferred stock" },
  { key: "current_ratio", label: "Current ratio" },
  { key: "debt_to_equity", label: "Debt / equity" },
  { key: "debt_to_assets", label: "Debt / assets" },
];

/* ── Cash flow — artifact order ── */
const CASHFLOW_ROWS = [
  { key: "net_income", label: "Net Income", bold: true },
  { key: "depreciation_amortization", label: "Depreciation Amortization" },
  { key: "stock_based_compensation", label: "Stock Based Compensation" },
  { key: "inventory_change", label: "Inventory Change" },
  { key: "accounts_payable_change", label: "Accounts Payable Change" },
  { key: "accounts_receivable_change", label: "Accounts Receivable Change" },
  { key: "other_working_capital", label: "Other working capital changes" },
  { key: "change_in_working_capital", label: "Change In Working Capital" },
  { key: "other_operating_activities", label: "Other Operating Activities" },
  { key: "operating_cash_flow", label: "Operating Cash Flow", bold: true },
  { key: "capital_expenditure", label: "Capital Expenditure" },
  { key: "investment_purchases", label: "Investment Purchases" },
  { key: "investment_sales", label: "Investment Sales" },
  { key: "acquisitions", label: "Acquisitions" },
  { key: "other_investing_activities", label: "Other Investing Activities" },
  { key: "investing_cash_flow", label: "Investing Cash Flow", bold: true },
  // the artifact renames this: it is ONE net series, not a repayment-only line
  { key: "debt_repayment", label: "Net Debt Issuance" },
  { key: "dividends_paid", label: "Dividends Paid" },
  { key: "stock_repurchased", label: "Stock Repurchased" },
  { key: "stock_issued", label: "Stock Issued" },
  { key: "other_financing_activities", label: "Other Financing Activities" },
  { key: "financing_cash_flow", label: "Financing Cash Flow", bold: true },
  { key: "net_change_in_cash", label: "Net Change In Cash", bold: true },
  { key: "free_cash_flow", label: "Free Cash Flow", bold: true },
];

const CASHFLOW_STORED = [
  { key: "deferred_income_tax", label: "Deferred income tax" },
  { key: "other_non_cash_items", label: "Other non-cash items" },
  { key: "effect_of_forex_changes_on_cash", label: "FX effect on cash" },
  { key: "cash_at_beginning_of_period", label: "Cash at beginning of period" },
  { key: "cash_at_end_of_period", label: "Cash at end of period" },
];

export const STATEMENTS = Object.freeze([
  { key: "income_statement", label: "Income statement", rows: INCOME_ROWS, stored: INCOME_STORED },
  { key: "balance_sheet", label: "Balance sheet", rows: BALANCE_ROWS, stored: BALANCE_STORED },
  { key: "cash_flow", label: "Cash flow", rows: CASHFLOW_ROWS, stored: CASHFLOW_STORED },
]);

/* Keys that are metadata, not line items — never rendered as rows.
   Includes the audit columns the Financial tab also excludes; without them
   "Updated At" and friends appeared as rows in the balance sheet and cash
   flow, where the API returns them. */
export const META_KEYS = Object.freeze([
  "fiscal_year", "period_type", "period_end_date", "is_projected",
  "ticker", "stock_id", "id", "currency", "calendar_year", "period",
  "created_at", "updated_at", "fetched_at", "last_updated", "as_of_date",
  "report_date", "filing_date", "accepted_date", "cik", "link", "final_link",
  "reported_currency", "symbol",
]);

export const prettifyKey = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/(^|\s)(ebitda|ebt|eps|roe|roa|ebit|fcfe|da|wc)(\s|$)/gi, (_, p1, p2, p3) => `${p1}${p2.toUpperCase()}${p3}`)
    .replace(/\b\w/g, (l) => l.toUpperCase());

/* Per-share / share-count rows must not be abbreviated as $B. */
export const isPerShareKey = (key) => /eps|per_share|shares_outstanding/i.test(key);

/* Share COUNTS, as opposed to per-share amounts (EPS, book value/share). */
export const isShareCountKey = (key) => /shares_outstanding|share_count|diluted_shares/i.test(key);

/**
 * Shares implied by the period itself: net income ÷ EPS. Used to pin down
 * which unit a share count was reported in — it is the only field in the
 * payload that states the answer independently.
 */
export const impliedShareCount = (period, { diluted = false } = {}) => {
  if (!period) return null;
  const ni = Number(period.net_income ?? period.bottom_line_net_income);
  const eps = Number(
    diluted
      ? period.eps_diluted ?? period.eps_basic ?? period.eps
      : period.eps_basic ?? period.eps ?? period.eps_diluted
  );
  if (!Number.isFinite(ni) || !Number.isFinite(eps) || eps === 0) return null;
  const n = Math.abs(ni / eps);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const SHARE_SCALES = [1, 1e3, 1e6, 1e9];

/**
 * Share counts arrive in THREE different units and the key does not say which:
 * the actuals report `shares_outstanding` in billions (0.28), the projections
 * report `shares_outstanding_diluted` in absolute shares (231,560,000), and
 * `shares_outstanding_m` is in millions. Rendering them as received puts
 * "0.28" next to "231.56M" in the same statement.
 *
 * Normalise everything to ABSOLUTE shares:
 *   1. when the period carries net income and EPS, pick the scale whose result
 *      lands closest to net income ÷ EPS — this separates "1.5 billion shares"
 *      from "1.5 million shares", which magnitude alone cannot;
 *   2. otherwise scale up in thousands until the value is a plausible share
 *      count (every company in coverage has at least a million shares).
 *
 *     0.28        → 280,000,000
 *     231.56      → 231,560,000
 *     231,560,000 → unchanged
 *
 * Zero and non-numbers pass straight through.
 *
 * @param {number} v
 * @param {{period?: object, key?: string}} [ctx]  the row's period and key,
 *        so the EPS cross-check can run and diluted rows use diluted EPS
 */
export const normalizeShareCount = (v, ctx = {}) => {
  const n = Number(v);
  if (v == null || !Number.isFinite(n) || n === 0) return v;

  const target = impliedShareCount(ctx.period, {
    diluted: /diluted/i.test(String(ctx.key ?? "")),
  });

  if (target) {
    let best = n;
    let bestErr = Infinity;
    SHARE_SCALES.forEach((s) => {
      const err = Math.abs(Math.abs(n * s) - target) / target;
      if (err < bestErr) {
        bestErr = err;
        best = n * s;
      }
    });
    /* diluted vs basic differ by a few percent at most — anything further out
       than a quarter means the cross-check is not describing this row */
    if (bestErr <= 0.25) return best;
  }

  let out = n;
  /* guard the loop: 1e3 → 1e6 → 1e9 → 1e12 is as far as any real unit goes */
  for (let i = 0; i < 4 && Math.abs(out) < 1e6; i += 1) out *= 1e3;
  return out;
};

export const isPercentKey = (key) =>
  ["margin", "yield", "return", "roe", "roa", "growth", "payout", "tax_rate", "ratio"].some((kw) =>
    String(key).includes(kw)
  );

/**
 * Build display rows for one statement:
 *   • aliases raw payload keys onto canonical ones
 *   • emits ONLY the artifact's visible rows, in the artifact's order,
 *     carrying `bold` — stored-in-DB rows and unknown payload keys are NOT
 *     rendered (2026-08-18; they are still received, just not displayed)
 *   • drops metadata keys and rows null in every period
 *
 * @param {Array<object>} periods  FY rows straight from the API
 * @param {object} statement       one entry from STATEMENTS
 */
export const buildStatementRows = (periods, statement) => {
  if (!Array.isArray(periods) || periods.length === 0) return [];

  const canon = periods.map((p) => {
    const out = {};
    Object.entries(p || {}).forEach(([k, v]) => {
      if (META_KEYS.includes(k)) return;
      out[KEY_ALIASES[k] ?? k] = v;
    });
    return out;
  });

  const present = new Set();
  canon.forEach((p) => Object.keys(p).forEach((k) => present.add(k)));

  return statement.rows
    .filter((r) => present.has(r.key))
    .map((spec) => ({
      key: spec.key,
      label: spec.label,
      bold: !!spec.bold,
      isPercent: isPercentKey(spec.key),
      isPerShare: isPerShareKey(spec.key),
      isShareCount: isShareCountKey(spec.key),
      values: canon.map((p) => (p[spec.key] === undefined ? null : p[spec.key])),
    }))
    .filter((row) => row.values.some((v) => v != null));
};
